import { getCollageTemplate } from '@/content/collageTemplates'
import { getFilmFormat } from '@/content/instantFilm'
import { collageCellRect } from '@/core/document/collage'
import { nativeSize, nodeRenderRect } from '@/core/document/tree'
import { NodeKind, type CompositionNode, type Document, type ImageNode, type SourceId } from '@/core/document/types'
import type { AffineMatrix, Size } from '@/core/geometry'
import { FrameStyleId, PhotoPlacement, plainFrameInnerSize } from '@/tools/frame/params'

export interface ImageWindow {
  image: ImageNode
  size: Size
  transform: AffineMatrix
}

const IDENTITY_MATRIX: Readonly<AffineMatrix> = { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 }

function multiply(first: AffineMatrix, second: AffineMatrix): AffineMatrix {
  return {
    a: first.a * second.a + first.c * second.b,
    b: first.b * second.a + first.d * second.b,
    c: first.a * second.c + first.c * second.d,
    d: first.b * second.c + first.d * second.d,
    e: first.a * second.e + first.c * second.f + first.e,
    f: first.b * second.e + first.d * second.f + first.f,
  }
}

function translate(x: number, y: number): AffineMatrix {
  return { a: 1, b: 0, c: 0, d: 1, e: x, f: y }
}

function rotate(degrees: number): AffineMatrix {
  const radians = degrees * Math.PI / 180
  const cosine = Math.cos(radians)
  const sine = Math.sin(radians)
  return { a: cosine, b: sine, c: -sine, d: cosine, e: 0, f: 0 }
}

export function scaleImageWindow(window: ImageWindow, scale: number, offsetX: number, offsetY: number): ImageWindow {
  if (!Number.isFinite(scale) || scale <= 0 || !Number.isFinite(offsetX) || !Number.isFinite(offsetY)) {
    throw new Error('Image window viewport transform must be finite with a positive scale')
  }
  return {
    ...window,
    transform: multiply(translate(offsetX, offsetY), multiply({ a: scale, b: 0, c: 0, d: scale, e: 0, f: 0 }, window.transform)),
  }
}

export function imageWindows(
  document: Document,
  sources: ReadonlyMap<SourceId, Size>,
  targetSize: Size,
  rootOffset: { x: number; y: number } = { x: 0, y: 0 },
): ImageWindow[] {
  const windows: ImageWindow[] = []

  function visitInContainer(
    node: CompositionNode,
    containerSize: Size,
    transform: AffineMatrix,
  ): void {
    const rect = nodeRenderRect(document, sources, node, containerSize)
    visit(
      node,
      { width: rect.width, height: rect.height },
      multiply(transform, translate(rect.x, rect.y)),
    )
  }

  function visit(node: CompositionNode, size: Size, transform: AffineMatrix): void {
    switch (node.kind) {
      case NodeKind.Image:
        windows.push({ image: node, size, transform })
        return
      case NodeKind.Frame:
        if (node.params.styleId === FrameStyleId.Plain) {
          const childSize = plainFrameInnerSize(size, node.params.borderMm)
          const inset = (size.width - childSize.width) / 2
          visitInContainer(node.child, childSize, multiply(transform, translate(inset, inset)))
          return
        }
        {
          const format = getFilmFormat(node.params.formatId)
          const pixelsPerMm = size.width / format.filmMm.width
          const window = {
            x: format.windowMm.x * pixelsPerMm,
            y: format.windowMm.y * pixelsPerMm,
            width: format.windowMm.width * pixelsPerMm,
            height: format.windowMm.height * pixelsPerMm,
          }
          if (node.params.placement === PhotoPlacement.Fill) {
            visitInContainer(
              node.child,
              { width: window.width, height: window.height },
              multiply(transform, translate(window.x, window.y)),
            )
            return
          }
          if (!node.params.free) throw new Error(`Frame "${node.id}" is missing Free placement parameters`)
          const childNativeSize = nativeSize(document, sources, node.child.id)
          const photoWidth = window.width * node.params.free.scale
          const photoHeight = photoWidth * childNativeSize.height / childNativeSize.width
          const centerX = window.x + window.width * (0.5 + node.params.free.offsetX)
          const centerY = window.y + window.height * (0.5 + node.params.free.offsetY)
          visitInContainer(
            node.child,
            { width: photoWidth, height: photoHeight },
            multiply(transform, translate(centerX - photoWidth / 2, centerY - photoHeight / 2)),
          )
          return
        }
      case NodeKind.Collage: {
        const template = getCollageTemplate(node.templateId)
        for (const [index, child] of node.cells.entries()) {
          if (!child) continue
          const cell = template.cells[index]
          if (!cell) throw new Error(`Collage "${node.id}" is missing template cell ${index + 1}`)
          const rect = collageCellRect(template, cell, size, node.gap)
          const cellTransform = multiply(
            transform,
            multiply(
              translate(rect.x + rect.width / 2, rect.y + rect.height / 2),
              multiply(rotate(cell.rotationDeg), translate(-rect.width / 2, -rect.height / 2)),
            ),
          )
          visitInContainer(child, { width: rect.width, height: rect.height }, cellTransform)
        }
        return
      }
      default:
        throw new Error(`Cannot resolve image windows for unknown node kind "${String((node as { kind: unknown }).kind)}"`)
    }
  }

  visit(document.root, targetSize, multiply(IDENTITY_MATRIX, translate(rootOffset.x, rootOffset.y)))
  return windows
}
